import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useCourse } from '../contexts/CourseContext';
import type { LearningActivity, LearnerResponse, ResponseData } from '../types/learningActivities';

interface UseActivityResponseResult {
  activity: LearningActivity | null;
  response: LearnerResponse | null;
  isLoading: boolean;
  error: string | null;
  saveResponse: (value: string | null, data?: ResponseData) => Promise<void>;
  isSaving: boolean;
}

/**
 * Hook to manage a single learning activity and its response
 * Handles fetching activity definition, loading existing response, and auto-saving
 */
export function useActivityResponse(activityKey: string): UseActivityResponseResult {
  const { currentCourse } = useCourse();
  const [activity, setActivity] = useState<LearningActivity | null>(null);
  const [response, setResponse] = useState<LearnerResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch activity definition and existing response
  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      if (!currentCourse) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Get user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('User not authenticated');
        }

        // Fetch activity definition
        const { data: activityData, error: activityError } = await supabase
          .from('learning_activities')
          .select('*')
          .eq('activity_key', activityKey)
          .single();

        if (activityError) throw activityError;
        if (!activityData) throw new Error(`Activity not found: ${activityKey}`);

        // Fetch existing response if any
        const { data: responseData, error: responseError } = await supabase
          .from('learner_responses')
          .select('*')
          .eq('user_id', user.id)
          .eq('course_id', currentCourse.id)
          .eq('activity_id', activityData.id)
          .maybeSingle();

        if (responseError) throw responseError;

        if (!cancelled) {
          setActivity(activityData as LearningActivity);
          setResponse(responseData as LearnerResponse | null);
        }
      } catch (err: any) {
        if (!cancelled) {
          console.error('[useActivityResponse] Error:', err);
          setError(err.message || 'Failed to load activity');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchData();
    return () => { cancelled = true; };
  }, [activityKey, currentCourse]);

  // Save response (upsert)
  const saveResponse = useCallback(async (value: string | null, data?: ResponseData) => {
    if (!activity || !currentCourse) {
      console.warn('[useActivityResponse] Cannot save: activity or course not loaded');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const payload = {
        user_id: user.id,
        course_id: currentCourse.id,
        activity_id: activity.id,
        response_value: value,
        response_data: data || {},
      };

      const { data: savedResponse, error: saveError } = await supabase
        .from('learner_responses')
        .upsert(payload, {
          onConflict: 'user_id,course_id,activity_id',
        })
        .select()
        .single();

      if (saveError) throw saveError;

      setResponse(savedResponse as LearnerResponse);
    } catch (err: any) {
      console.error('[useActivityResponse] Save error:', err);
      setError(err.message || 'Failed to save response');
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [activity, currentCourse]);

  return {
    activity,
    response,
    isLoading,
    error,
    saveResponse,
    isSaving,
  };
}
